// pages/components/settings/Documents.jsx
import React, { useState, useEffect } from 'react';
import { Upload, FileText, Award, Download, Trash2, Edit2, Plus, X, Save } from 'lucide-react';
import apiRequest from '@/services/requests';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { confirmToast } from '@/utils/confirmToast';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';



const Documents = () => { 
const [documents, setDocuments] = useState([]);
const [loading, setLoading] = useState(true);
const [uploading, setUploading] = useState(false);
const [showUploadForm, setShowUploadForm] = useState(false);
const [formData, setFormData] = useState({
type: 'certificate',
title: '',
description: '',
issuing_organization: '',
issue_date: '',
expiry_date: '',
credential_id: '',
credential_url: ''
});
const [selectedFile, setSelectedFile] = useState(null);

useEffect(() => {
loadDocuments();
}, []);

const loadDocuments = async () => {
setLoading(true);
try {
const res = await apiRequest.getDocuments({ status: 'active' });
if (res.success) {
setDocuments(res.data);
}
} catch (error) {
console.error('Error loading documents:', error);
}
setLoading(false);
};

const handleFileSelect = (e) => {
const file = e.target.files[0];
if (file) {
// Validate file size (5MB)
if (file.size > 5 * 1024 * 1024) {
toast.warning('File size must be less than 5MB');
return;
}
setSelectedFile(file);
}
};

const handleInputChange = (e) => {
const { name, value } = e.target;
setFormData(prev => ({ ...prev, [name]: value }));
};

const handleUpload = async (e) => {
e.preventDefault();

if (!selectedFile) {
toast.warning('Please select a file');
return;
}

if (!formData.title || !formData.type) {
toast.warning('Please fill in required fields');
return;
}

setUploading(true);
try {
const res = await apiRequest.uploadDocument(selectedFile, formData);
if (res.success) {
toast.success('Document uploaded successfully');
setShowUploadForm(false);
resetForm();
loadDocuments();
}
} catch (error) {
console.error('Error uploading document:', error);
toast.error('Failed to upload document');
}
setUploading(false);
};

// const handleDelete = async (documentId) => {
// // if (!confirm('Are you sure you want to delete this document?')) return;

// try {
// await apiRequest.deleteDocument(documentId);
// loadDocuments();
// } catch (error) {
// console.error('Error deleting document:', error);
// toast.error('Failed to delete document');

// }
// };

const handleDelete = (documentId) => {
confirmToast({
message: 'Are you sure you want to delete this document?',
onConfirm: async () => {
try {
await apiRequest.deleteDocument(documentId);
loadDocuments();
toast.success('Document deleted');
} catch (error) {
toast.error('Failed to delete document');
}
}
});
};


const resetForm = () => {
setFormData({
type: 'certificate',
title: '',
description: '',
issuing_organization: '',
issue_date: '',
expiry_date: '',
credential_id: '',
credential_url: ''
});
setSelectedFile(null);
};

const documentTypes = [
{ value: 'certificate', label: 'Certificate', icon: Award },
{ value: 'award', label: 'Award', icon: Award },
{ value: 'license', label: 'License', icon: FileText },
{ value: 'transcript', label: 'Transcript', icon: FileText },
{ value: 'portfolio', label: 'Portfolio', icon: FileText },
{ value: 'reference_letter', label: 'Reference Letter', icon: FileText },
{ value: 'diploma', label: 'Diploma', icon: FileText },
{ value: 'degree', label: 'Degree Certificate', icon: FileText },
{ value: 'id_document', label: 'National ID / Identity Document', icon: FileText },
{ value: 'passport', label: 'Passport', icon: FileText },
{ value: 'work_permit', label: 'Work Permit', icon: FileText },
{ value: 'visa', label: 'Visa Document', icon: FileText },
{ value: 'driving_license', label: 'Driving License', icon: FileText },
{ value: 'recommendation_letter', label: 'Recommendation Letter', icon: FileText },
{ value: 'background_check', label: 'Background Check', icon: FileText },
{ value: 'medical_certificate', label: 'Medical Certificate', icon: FileText },
{ value: 'security_clearance', label: 'Security Clearance', icon: FileText },
{ value: 'publications', label: 'Publications', icon: FileText },
{ value: 'projects', label: 'Project Documentation', icon: FileText },
{ value: 'employment_contract', label: 'Previous Employment Contract', icon: FileText },
{ value: 'payslip', label: 'Payslip', icon: FileText },
{ value: 'tax_document', label: 'Tax Document', icon: FileText },
{ value: 'nda', label: 'Non-Disclosure Agreement (NDA)', icon: FileText },
{ value: 'other', label: 'Other', icon: FileText }
];

const getDocumentIcon = (type) => {
const docType = documentTypes.find(t => t.value === type);
const Icon = docType?.icon || FileText;
return <Icon size={24} />;
};

if (loading) {
return (
<div className="flex items-center justify-center py-12">
<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
</div>
);
}

return (
<div className="space-y-6 relative">

<div className="flex items-center justify-between">
<div>
<h2 className="text-lg lg:text-2xl font-bold text-slate-900">Professional Documents</h2>
<p className="text-slate-600 mt-1 text-sm">Upload certificates, awards, and other credentials</p>
</div>

{!showUploadForm && (
<button
onClick={() => setShowUploadForm(true)}
className="hidden lg:flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg shadow-md transition-all"
>
<Plus size={20} />
Upload Document
</button>
)}



{/* Mobile Action Buttons */}
{!showUploadForm && (<div className="md:hidden fixed top-0 right-[-6%] flex flex-col space-y-3">
<Button
variant="default"
size="icon"
className="w-10 h-10 rounded-full shadow-elevation-2"
onClick={() => setShowUploadForm(true)}
>
<Icon name="Plus" size={20} />
</Button>
</div>)}


</div>





{/* Upload Form */}
{showUploadForm && (
<div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-xl border-2 border-indigo-200">
<div className="flex items-center justify-between mb-4">
<h3 className="text-lg font-bold text-slate-900">Upload New Document</h3>
<button
onClick={() => {
setShowUploadForm(false);
resetForm();
}}
className="text-slate-500 hover:text-slate-700"
>
<X size={24} />
</button>
</div>

<form onSubmit={handleUpload} className="space-y-4">
{/* Document Type */}
<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Document Type *
</label>
<select
name="type"
value={formData.type}
onChange={handleInputChange}
required
className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500"
>
{documentTypes.map(type => (
<option key={type.value} value={type.value}>
{type.label}
</option>
))}
</select>
</div>

{/* Title */}
<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Title *
</label>
<input
type="text"
name="title"
value={formData.title}
onChange={handleInputChange}
required
placeholder="e.g., AWS Certified Solutions Architect"
className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500"
/>
</div>

{/* File Upload */}
<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
File * (Max 5MB - PDF, JPG, PNG)
</label>
<div className="flex items-center gap-3">
<label className="flex-1 flex items-center gap-3 px-4 py-3 bg-white rounded-lg border-2 border-dashed border-slate-300 hover:border-indigo-400 cursor-pointer transition-all">
<Upload className="text-slate-400" size={20} />
<span className="text-sm text-slate-600">
{selectedFile ? selectedFile.name : 'Choose a file...'}
</span>
<input
type="file"
onChange={handleFileSelect}
accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
className="hidden"
required
/>
</label>
</div>
</div>

{/* Additional Fields */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Issuing Organization
</label>
<input
type="text"
name="issuing_organization"
value={formData.issuing_organization}
onChange={handleInputChange}
placeholder="e.g., Amazon Web Services"
className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500"
/>
</div>

<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Credential ID
</label>
<input
type="text"
name="credential_id"
value={formData.credential_id}
onChange={handleInputChange}
placeholder="e.g., ABC123XYZ"
className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500"
/>
</div>

<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Issue Date
</label>
<input
type="date"
name="issue_date"
value={formData.issue_date}
onChange={handleInputChange}
className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500"
/>
</div>

<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Expiry Date (if applicable)
</label>
<input
type="date"
name="expiry_date"
value={formData.expiry_date}
onChange={handleInputChange}
className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500"
/>
</div>
</div>

<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Credential URL
</label>
<input
type="url"
name="credential_url"
value={formData.credential_url}
onChange={handleInputChange}
placeholder="https://..."
className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500"
/>
</div>

<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Description
</label>
<textarea
name="description"
value={formData.description}
onChange={handleInputChange}
rows={3}
placeholder="Brief description of this credential..."
className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 resize-none"
/>
</div>

{/* Submit Button */}
<button
type="submit"
disabled={uploading}
className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-semibold rounded-lg shadow-md transition-all"
>
{uploading ? (
<>
<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
Uploading...
</>
) : (
<>
<Upload size={20} />
Upload Document
</>
)}
</button>
</form>
</div>
)}

{/* Documents Grid */}
{documents.length === 0 ? (
<div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
<FileText className="mx-auto text-slate-300 mb-4" size={48} />
<h3 className="text-lg font-semibold text-slate-900 mb-2">No Documents Yet</h3>
<p className="text-slate-600">Upload your professional certificates, awards, and credentials</p>
</div>
) : (
<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
{documents.map(doc => (
<div
key={doc.id}
className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-all"
>
<div className="flex items-start gap-4">
<div className="p-3 bg-indigo-100 rounded-lg text-indigo-600">
{getDocumentIcon(doc.type)}
</div>

<div className="flex-1 min-w-0">
<h3 className="font-bold text-slate-900 mb-1">{doc.title}</h3>
<p className="text-sm text-slate-600 mb-2">
{documentTypes.find(t => t.value === doc.type)?.label}
</p>

{doc.issuing_organization && (
<p className="text-xs text-slate-500 mb-1">
Issued by: {doc.issuing_organization}
</p>
)}

{doc.issue_date && (
<p className="text-xs text-slate-500">
Issued: {new Date(doc.issue_date).toLocaleDateString()}
</p>
)}

{doc.credential_url && (
<a
href={doc.credential_url}
target="_blank"
rel="noopener noreferrer"
className="text-xs text-indigo-600 hover:text-indigo-700 mt-2 inline-block"
>
View Credential →
</a>
)}
</div>

<div className="flex gap-2">
<button
onClick={() => handleDelete(doc.id)}
className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
title="Delete"
>
<Trash2 size={18} />
</button>
</div>
</div>
</div>
))}
</div>
)}
</div>
);
};

export default Documents;